import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { signInWithEmail, signInWithGoogle, signInWithApple, sendPasswordReset } from '@shared/services/auth';
import { app, auth } from '@shared/services/firebase';
import { useAuthStore } from '@shared/state';
import { Button } from '../components/ui/Button';

// Look up the freshly-signed-in user's role and route admins to the
// admin dashboard. Falls back to /dashboard for any other role or on
// lookup failure (admin gate is enforced server-side anyway).
async function postLoginPath(): Promise<string> {
  const uid = auth.currentUser?.uid;
  if (!uid) return '/dashboard';
  try {
    const snap = await getDoc(doc(getFirestore(app), 'users', uid));
    if (snap.data()?.role === 'admin') return '/admin-dashboard';
  } catch {
    /* fall through to /dashboard */
  }
  return '/dashboard';
}

type Mode = 'signin' | 'reset';

export function Login() {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const setError = useAuthStore((s) => s.setError);
  const error = useAuthStore((s) => s.error);
  const navigate = useNavigate();

  const handleEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signInWithEmail(email, password);
      navigate(await postLoginPath());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithGoogle();
      navigate(await postLoginPath());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApple = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithApple();
      navigate(await postLoginPath());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Apple sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await sendPasswordReset(email.trim());
      setResetSent(true);
    } catch (err) {
      // Don't leak whether the email is registered — show a neutral success
      // unless this is a clearly malformed email.
      const message = err instanceof Error ? err.message : 'Failed to send reset email';
      if (message.includes('invalid-email')) {
        setError('That email address doesn\'t look right.');
      } else {
        // Treat as success to avoid account enumeration
        setResetSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
    setResetSent(false);
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'linear-gradient(135deg, #0B0E1A, #111528, #0E1225)' }}
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <div
          className="w-full rounded-2xl p-7 backdrop-blur-xl"
          style={{
            background: 'linear-gradient(145deg, rgba(20,24,48,0.85), rgba(14,18,37,0.65))',
            border: '1px solid rgba(108,92,231,0.2)',
            boxShadow:
              '0 25px 60px -12px rgba(0,0,0,0.7), ' +
              '0 0 80px -20px rgba(108,92,231,0.15), ' +
              'inset 0 1px 0 rgba(255,255,255,0.06), ' +
              'inset 0 -1px 0 rgba(0,0,0,0.3)',
          }}
        >
          {mode === 'signin' ? (
            <>
              <h1 className="font-heading text-xl font-bold text-center mb-1">Welcome back</h1>
              <p className="text-content-muted text-xs text-center mb-6">Sign in to your CogniPrint account</p>

              {error && (
                <div
                  className="rounded-lg text-sm p-3 mb-4 text-center"
                  style={{
                    background: 'rgba(239,68,68,0.1)',
                    border: '1px solid rgba(239,68,68,0.25)',
                    color: '#f87171',
                  }}
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleEmail}>
                <label className="block text-[11px] uppercase tracking-wider text-content-muted mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full rounded-lg text-sm p-2.5 mb-4 bg-dark-subtle border border-dark-border text-content focus:border-indigo-500 focus:outline-none transition-colors"
                />

                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[11px] uppercase tracking-wider text-content-muted">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => switchMode('reset')}
                    className="text-[11px] text-indigo-400 hover:text-indigo-300 transition-colors cursor-pointer"
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full rounded-lg text-sm p-2.5 mb-5 bg-dark-subtle border border-dark-border text-content focus:border-indigo-500 focus:outline-none transition-colors"
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-lg font-semibold text-sm py-2.5 text-white disabled:opacity-50 transition-all"
                  style={{ background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)' }}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Signing in…
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-dark-border" />
                <span className="text-xs text-content-dim">or</span>
                <div className="flex-1 h-px bg-dark-border" />
              </div>

              <button
                onClick={handleGoogle}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2.5 rounded-lg text-sm font-medium py-2.5 border border-dark-border bg-dark-subtle text-content hover:bg-white/5 disabled:opacity-50 transition-all"
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>

              <button
                onClick={handleApple}
                disabled={loading}
                className="mt-2 w-full flex items-center justify-center gap-2.5 rounded-lg text-sm font-medium py-2.5 border border-dark-border bg-dark-subtle text-content hover:bg-white/5 disabled:opacity-50 transition-all"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M16.365 1.43c0 1.14-.43 2.21-1.18 3.02-.81.87-2.15 1.55-3.27 1.46-.13-1.13.43-2.3 1.16-3.07.84-.86 2.27-1.5 3.29-1.41zM20.5 17.13c-.55 1.27-.81 1.84-1.51 2.96-.99 1.58-2.39 3.55-4.13 3.56-1.55.02-1.95-1.01-4.05-1-2.1.01-2.54 1.02-4.09 1-1.74-.02-3.07-1.79-4.06-3.37-2.77-4.43-3.06-9.62-1.35-12.39 1.21-1.96 3.13-3.11 4.93-3.11 1.83 0 2.99 1 4.5 1 1.47 0 2.36-1.01 4.49-1.01 1.6 0 3.3.87 4.51 2.38-3.96 2.17-3.31 7.83.76 9.98z"/>
                </svg>
                Continue with Apple
              </button>

              <p className="mt-6 text-center text-sm text-content-muted font-body">
                Don't have an account?{' '}
                <Link to="/signup" className="text-indigo-400 hover:text-indigo-300 font-medium transition-colors">
                  Sign up
                </Link>
              </p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold text-center mb-2">Reset your password</h1>
              <p className="text-sm text-content-muted text-center font-body mb-6">
                Enter your email and we'll send you a reset link.
              </p>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm font-body">
                  {error}
                </div>
              )}

              {resetSent ? (
                <div className="text-center">
                  <div className="mb-4 p-3 rounded-lg bg-success/10 border border-success/20 text-success text-sm font-body">
                    If an account exists for <span className="font-semibold">{email}</span>, a reset link is on its way.
                  </div>
                  <button
                    onClick={() => switchMode('signin')}
                    className="text-sm text-indigo-400 hover:text-indigo-300 font-body cursor-pointer transition-colors"
                  >
                    Back to sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={handleReset} className="space-y-4">
                  <div>
                    <label htmlFor="reset-email" className="block text-sm font-medium font-body text-content-secondary mb-1">
                      Email
                    </label>
                    <input
                      id="reset-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-3 py-2 rounded-lg border border-dark-border bg-dark-subtle text-content text-sm font-body focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/40 transition-colors"
                    />
                  </div>
                  <Button className="w-full" loading={loading} type="submit">
                    Send reset link
                  </Button>
                  <button
                    type="button"
                    onClick={() => switchMode('signin')}
                    className="w-full text-sm text-content-muted hover:text-content font-body cursor-pointer transition-colors"
                  >
                    Back to sign in
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
